/* ============================================================
   Strange Shrew — theme.js
   Vanilla JS, no framework, no external icon/animation library.
   Modules: drawers, header, mega menu, hero slideshow, reveal,
   accordions, carousels, ajax cart, predictive search, PDP.
   ============================================================ */
(function () {
  'use strict';

  var CFG = {};
  try {
    CFG = JSON.parse(document.getElementById('theme-settings').textContent);
  } catch (e) { CFG = { routes: {} }; }
  var ROUTES = CFG.routes || {};
  var STR = CFG.strings || {};

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }
  /* shared by the PDP variant picker and the product-card quick-add —
     both need to turn a variant's price (in cents) into the shop's
     formatted money string without a round-trip to the server */
  function formatMoney(cents) {
    var amount = (cents / 100).toFixed(2);
    var format = CFG.moneyFormat || '₹{{amount}}';
    return format.replace(/\{\{\s*amount\s*\}\}/, amount).replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100));
  }
  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  /* ---------------------------------------------------------
     Drawer manager (menu / cart / search) + overlay + body lock
     --------------------------------------------------------- */
  var overlay = qs('#overlay');
  var panels = {
    menu: qs('#menu-drawer'),
    cart: qs('#cart-drawer'),
    search: qs('#search-panel'),
    filters: qs('#filters')
  };
  var openPanelName = null;
  var lastFocusedEl = null;
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function openDrawer(name) {
    if (!panels[name]) return;
    lastFocusedEl = document.activeElement;
    closeDrawer();
    openPanelName = name;
    panels[name].classList.add('open');
    overlay.classList.add('show');
    document.body.classList.add('locked');
    if (name === 'cart') loadCartUpsell();
    setTimeout(function () {
      var input = name === 'search' ? qs('#search-input', panels.search) : null;
      var target = input || panels[name].querySelector(FOCUSABLE);
      if (target) target.focus();
    }, 350);
  }
  function closeDrawer() {
    if (!openPanelName) return;
    panels[openPanelName].classList.remove('open');
    overlay.classList.remove('show');
    document.body.classList.remove('locked');
    openPanelName = null;
    if (lastFocusedEl && typeof lastFocusedEl.focus === 'function') lastFocusedEl.focus();
    lastFocusedEl = null;
  }
  document.addEventListener('click', function (e) {
    var openBtn = e.target.closest('[data-drawer-open]');
    if (openBtn) { e.preventDefault(); openDrawer(openBtn.getAttribute('data-drawer-open')); return; }
    var closeBtn = e.target.closest('[data-drawer-close]');
    if (closeBtn) { e.preventDefault(); closeDrawer(); }
  });
  if (overlay) overlay.addEventListener('click', closeDrawer);
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') { closeDrawer(); return; }
    /* trap Tab focus inside the open drawer so keyboard users can't tab
       out to content hidden behind the overlay */
    if (e.key === 'Tab' && openPanelName && panels[openPanelName]) {
      var focusables = qsa(FOCUSABLE, panels[openPanelName]).filter(function (el) { return el.offsetParent !== null; });
      if (!focusables.length) return;
      var first = focusables[0], last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });
  qsa('.mob-link').forEach(function (a) { a.addEventListener('click', closeDrawer); });

  /* ---------------------------------------------------------
     Accordions (mobile menu + PDP info panels)
     --------------------------------------------------------- */
  qsa('.acc-btn').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var body = btn.nextElementSibling;
      if (!body) return;
      var isOpen = body.classList.toggle('open');
      btn.classList.toggle('open', isOpen);
      btn.setAttribute('aria-expanded', isOpen);
    });
  });

  /* ---------------------------------------------------------
     Product-card hover image: the second image only gets a src the first
     time a pointer enters the card, so phones and cards nobody hovers
     never download it. Keyboard focus does the same.
     --------------------------------------------------------- */
  function hydrateHoverImage(card) {
    var img = card && card.querySelector('img[data-hover-src]');
    if (!img) return;
    var set = img.getAttribute('data-hover-srcset');
    if (set) img.setAttribute('srcset', set);
    img.setAttribute('src', img.getAttribute('data-hover-src'));
    img.removeAttribute('data-hover-src');
    img.removeAttribute('data-hover-srcset');
  }
  if (window.matchMedia && window.matchMedia('(hover: hover)').matches) {
    document.addEventListener('mouseover', function (e) {
      var card = e.target.closest && e.target.closest('.p-card');
      if (card) hydrateHoverImage(card);
    }, { passive: true });
    document.addEventListener('focusin', function (e) {
      var card = e.target.closest && e.target.closest('.p-card');
      if (card) hydrateHoverImage(card);
    });
  }

  /* ---------------------------------------------------------
     FAQ section: live filter over question + answer text
     --------------------------------------------------------- */
  qsa('[data-faq-filter]').forEach(function (input) {
    var root = input.closest('section') || document;
    var items = qsa('[data-faq-item]', root);
    var cats = qsa('[data-faq-category]', root);
    var empty = qs('[data-faq-empty]', root);
    var run = debounce(function () {
      var term = input.value.trim().toLowerCase();
      var shown = 0;
      items.forEach(function (item) {
        var hit = !term || item.textContent.toLowerCase().indexOf(term) > -1;
        item.style.display = hit ? '' : 'none';
        if (hit) shown++;
        if (hit && term) {
          var body = qs('.acc-body', item), btn = qs('.acc-btn', item);
          if (body && btn) { body.classList.add('open'); btn.classList.add('open'); btn.setAttribute('aria-expanded', 'true'); }
        }
      });
      cats.forEach(function (cat) {
        var next = cat.nextElementSibling, any = false;
        while (next && !next.hasAttribute('data-faq-category')) {
          if (next.hasAttribute('data-faq-item') && next.style.display !== 'none') { any = true; break; }
          next = next.nextElementSibling;
        }
        cat.style.display = any ? '' : 'none';
      });
      if (empty) empty.classList.toggle('hidden', shown > 0);
    }, 120);
    input.addEventListener('input', run);
  });

  /* ---------------------------------------------------------
     Header: scroll state + mega menu
     --------------------------------------------------------- */
  var header = qs('[data-header]');
  if (header) {
    /* No transparent-over-hero header anymore — the header is always
       solid and fixed, on every page including the homepage, so every
       page (hero included) reserves space for the announcement bar +
       header's combined height instead of floating content under it. */
    document.body.classList.add('has-fixed-header-space');
    header.classList.add('is-solid');

    /* Pin the fixed header to sit exactly below the (also fixed)
       announcement bar, so it always sits flush underneath it with no gap
       and no double-counted offset, on every page. */
    var announcementBar = qs('.announcement-bar');
    function setAnnouncementOffset() {
      var h = announcementBar ? announcementBar.getBoundingClientRect().height : 0;
      document.documentElement.style.setProperty('--announcement-height', h + 'px');
    }
    setAnnouncementOffset();
    window.addEventListener('resize', debounce(setAnnouncementOffset, 150));

    /* Header stays fixed and visible at all times — no hide-on-scroll-down
       behavior. (An earlier reveal-on-scroll-up pattern slid the header
       itself away on scroll-down, but the announcement bar above it has
       no matching hide state and stayed pinned, leaving the marquee
       floating with the header gone. Since the header is meant to be
       fixed in place everywhere, the fix is to just never hide it —
       not to also animate the announcement bar.) Only the compact
       "scrolled" height state remains. */
    var onScroll = function () {
      var y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 60);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    /* Images inside a closed mega panel are pre-rendered with their src
       parked in data-src, so a page load never downloads menu products the
       shopper may never look at. Swapped in the first time a panel opens. */
    var hydrateLazyImages = function (root) {
      qsa('img[data-src]', root).forEach(function (img) {
        if (img.getAttribute('data-srcset')) { img.setAttribute('srcset', img.getAttribute('data-srcset')); img.removeAttribute('data-srcset'); }
        img.setAttribute('src', img.getAttribute('data-src'));
        img.removeAttribute('data-src');
      });
    };

    /* Full mega panels — there can be more than one now (any top-level
       nav item whose menu structure goes 3 levels deep automatically
       qualifies, see header.liquid), each wired up independently: hover
       opens/closes with a short close delay so crossing the gap between
       trigger and panel doesn't flicker it shut, click toggles it for
       touch/keyboard, Escape and an outside click both close it, and
       opening one closes any other that's open. */
    var megaTriggers = qsa('[data-mega-trigger]');
    if (megaTriggers.length) {
      var closeAllMega = function (immediate) {
        megaTriggers.forEach(function (t) {
          if (t._megaClose) t._megaClose(immediate);
        });
      };
      megaTriggers.forEach(function (trigger) {
        var panelId = trigger.getAttribute('aria-controls');
        var mega = panelId ? document.getElementById(panelId) : null;
        if (!mega) return;
        var megaItem = trigger.closest('.nav-item--mega') || trigger.parentElement;
        var megaTimer;
        var megaOpen = function () {
          closeAllMega(true);
          clearTimeout(megaTimer);
          hydrateLazyImages(mega);
          mega.classList.add('open');
          header.classList.add('mega-solid');
          trigger.setAttribute('aria-expanded', 'true');
          if (megaItem) megaItem.classList.add('is-open');
        };
        var megaClose = function (immediate) {
          clearTimeout(megaTimer);
          var run = function () {
            mega.classList.remove('open');
            header.classList.remove('mega-solid');
            trigger.setAttribute('aria-expanded', 'false');
            if (megaItem) megaItem.classList.remove('is-open');
          };
          if (immediate) { run(); } else { megaTimer = setTimeout(run, 220); }
        };
        trigger._megaClose = megaClose;
        trigger.addEventListener('mouseenter', megaOpen);
        trigger.addEventListener('mouseleave', function () { megaClose(); });
        mega.addEventListener('mouseenter', function () { clearTimeout(megaTimer); });
        mega.addEventListener('mouseleave', function () { megaClose(); });
        trigger.addEventListener('click', function (e) {
          e.stopPropagation();
          if (mega.classList.contains('open')) megaClose(true); else megaOpen();
        });
        trigger.addEventListener('keydown', function (e) {
          if (e.key === 'Escape') { megaClose(true); trigger.focus(); }
        });
        mega.addEventListener('click', function (e) { e.stopPropagation(); });
      });
      document.addEventListener('click', function () { closeAllMega(true); });
    }

    /* Simple auto-dropdowns (any other top-level item with sub-links) —
       CSS already opens these on plain :hover; this layer adds click-to-
       toggle for touch/keyboard, Escape, and outside-click-to-close, and
       makes sure opening one closes any other that's open. */
    var dropItems = qsa('.nav-item--drop');
    if (dropItems.length) {
      var closeAllDrops = function () {
        dropItems.forEach(function (item) {
          item.classList.remove('is-open');
          var t = qs('[data-drop-trigger]', item);
          if (t) t.setAttribute('aria-expanded', 'false');
        });
      };
      dropItems.forEach(function (item) {
        var dTrigger = qs('[data-drop-trigger]', item);
        if (!dTrigger) return;
        dTrigger.addEventListener('click', function (e) {
          e.stopPropagation();
          var willOpen = !item.classList.contains('is-open');
          closeAllDrops();
          item.classList.toggle('is-open', willOpen);
          dTrigger.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
        });
        dTrigger.addEventListener('keydown', function (e) {
          if (e.key === 'Escape') { closeAllDrops(); dTrigger.focus(); }
        });
      });
      document.addEventListener('click', closeAllDrops);
    }

    /* Tabbed dropdown panels: the collection tabs down the left switch the
       product grid on the right. The first tab is server-rendered; any
       other tab is fetched once through the Section Rendering API
       (/collections/<handle>?section_id=mega-menu-products) and cached for
       the rest of the page view. A short hover-intent delay stops the grid
       from flickering while the pointer travels down the tab list. */
    var megaTabCache = {};
    qsa('[data-mega-tabs]').forEach(function (panel) {
      var grid = qs('[data-mega-products-grid]', panel);
      var body = qs('[data-mega-products]', panel);
      var tabs = qsa('[data-mega-tab]', panel);
      if (!grid || !tabs.length) return;
      var intentTimer;
      var current = tabs[0];
      var firstHandle = current.getAttribute('data-handle');
      if (firstHandle) megaTabCache[firstHandle] = grid.innerHTML;

      var emptyTile = function (tab) {
        return '<a href="' + escapeHtml(tab.getAttribute('href')) + '" class="mega-empty">' +
          '<span class="mega-empty-label ls-mid uppercase">' + escapeHtml(tab.getAttribute('data-title') || '') + '</span>' +
          '<span class="mega-empty-cta ls-wide uppercase">' + escapeHtml((CFG.strings && CFG.strings.megaEmpty) || 'Explore') + ' &rarr;</span></a>';
      };
      var render = function (html) {
        grid.innerHTML = html;
        hydrateLazyImages(grid);
        body.classList.remove('is-loading');
      };
      var setActive = function (tab) {
        tabs.forEach(function (t) {
          var on = t === tab;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
        });
      };
      var activate = function (tab) {
        if (tab === current) return;
        current = tab;
        setActive(tab);
        var handle = tab.getAttribute('data-handle');
        if (!handle) { render(emptyTile(tab)); return; }
        if (megaTabCache[handle]) { render(megaTabCache[handle]); return; }
        body.classList.add('is-loading');
        var url = (ROUTES.collectionsUrl || '/collections') + '/' + encodeURIComponent(handle) + '?section_id=mega-menu-products';
        fetch(url)
          .then(function (r) { return r.text(); })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var fresh = doc.querySelector('[data-mega-products-grid]');
            var out = fresh && fresh.innerHTML.trim() ? fresh.innerHTML : emptyTile(tab);
            megaTabCache[handle] = out;
            if (current === tab) render(out);
          })
          .catch(function () { body.classList.remove('is-loading'); });
      };
      tabs.forEach(function (tab, i) {
        tab.addEventListener('mouseenter', function () {
          clearTimeout(intentTimer);
          intentTimer = setTimeout(function () { activate(tab); }, 90);
        });
        tab.addEventListener('mouseleave', function () { clearTimeout(intentTimer); });
        tab.addEventListener('focus', function () { activate(tab); });
        tab.addEventListener('keydown', function (e) {
          var next = null;
          if (e.key === 'ArrowDown') next = tabs[(i + 1) % tabs.length];
          if (e.key === 'ArrowUp') next = tabs[(i - 1 + tabs.length) % tabs.length];
          if (e.key === 'Home') next = tabs[0];
          if (e.key === 'End') next = tabs[tabs.length - 1];
          if (next) { e.preventDefault(); next.focus(); }
        });
      });
    });
  }

  /* ---------------------------------------------------------
     Hero slideshow
     --------------------------------------------------------- */
  qsa('[data-hero]').forEach(function (hero) {
    var slides = qsa('.hslide', hero);
    if (!slides.length) return;
    if (slides.length <= 1 || hero.hasAttribute('data-hero-single')) return; /* single image — no slider wiring needed, it's just shown via the .active class from Liquid */
    var dotsWrap = qs('[data-hero-dots]', hero);
    if (dotsWrap) {
      dotsWrap.innerHTML = slides.map(function (_, i) {
        return '<button class="h-dot' + (i === 0 ? ' on' : '') + '" data-slide="' + i + '" aria-label="' + escapeHtml((STR.slideN || 'Go to slide __N__').replace('__N__', i + 1)) + '"></button>';
      }).join('');
    }
    var dots = dotsWrap ? qsa('.h-dot', dotsWrap) : [];
    var idx = 0, timer;
    function setSlide(i) {
      idx = (i + slides.length) % slides.length;
      slides.forEach(function (s, j) { s.classList.toggle('active', j === idx); });
      dots.forEach(function (d, j) { d.classList.toggle('on', j === idx); });
    }
    var prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    function play() {
      clearInterval(timer);
      if (prefersReducedMotion && CFG.respectReducedMotion !== false) return; /* don't force-cycle slides for users who asked for reduced motion */
      timer = setInterval(function () { setSlide(idx + 1); }, 6000);
    }
    function go(i) { setSlide(i); play(); }
    var prev = qs('[data-hero-prev]', hero), next = qs('[data-hero-next]', hero);
    if (prev) prev.addEventListener('click', function () { go(idx - 1); });
    if (next) next.addEventListener('click', function () { go(idx + 1); });
    dots.forEach(function (d) { d.addEventListener('click', function () { go(+d.dataset.slide); }); });
    var touchX = null;
    hero.addEventListener('touchstart', function (e) { touchX = e.touches[0].clientX; }, { passive: true });
    hero.addEventListener('touchend', function (e) {
      if (touchX === null) return;
      var dx = e.changedTouches[0].clientX - touchX;
      if (Math.abs(dx) > 50) go(dx < 0 ? idx + 1 : idx - 1);
      touchX = null;
    }, { passive: true });

    /* desktop mouse support: click-and-drag to swipe, same as touch. Wheel-
       based slide changing was tried and removed — it required hijacking
       (preventDefault-ing) the visitor's normal vertical mouse wheel the
       instant their cursor was anywhere over the hero, which meant the
       page simply stopped scrolling whenever someone scrolled over a
       banner (worse than not having wheel support at all). Drag alone
       covers "scrollable by mouse" without that tradeoff. */
    var mouseDownX = null, mouseDragging = false;
    hero.addEventListener('mousedown', function (e) {
      /* don't start swipe-tracking when the press lands on a real link/
         button inside the slide (the caption CTA, or the whole-slide
         link-wrap used when the caption is hidden) — otherwise the drag
         handling can end up swallowing the click that should follow */
      if (e.target.closest('a, button')) return;
      mouseDownX = e.clientX; mouseDragging = true; hero.classList.add('is-dragging');
    });
    window.addEventListener('mouseup', function (e) {
      if (!mouseDragging) return;
      mouseDragging = false; hero.classList.remove('is-dragging');
      if (mouseDownX === null) return;
      var dx = e.clientX - mouseDownX;
      if (Math.abs(dx) > 60) go(dx < 0 ? idx + 1 : idx - 1);
      mouseDownX = null;
    });

    document.addEventListener('visibilitychange', function () { document.hidden ? clearInterval(timer) : play(); });
    play();
  });

  /* ---------------------------------------------------------
     Reveal on scroll
     --------------------------------------------------------- */
  /* Exposed as a function (rather than a one-shot pass at load) because
     content can arrive after first paint — the product recommendations
     carousel is fetched from Shopify's recommendations API and swapped in.
     Anything with .reveal inside newly inserted markup would otherwise sit
     at opacity:0 forever, since nothing was observing it. */
  var revealIO = null;
  if ('IntersectionObserver' in window) {
    revealIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) { if (en.isIntersecting) { en.target.classList.add('in'); revealIO.unobserve(en.target); } });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
  }
  function initReveal(root) {
    qsa('.reveal', root || document).forEach(function (el) {
      if (revealIO) { revealIO.observe(el); } else { el.classList.add('in'); }
    });
  }
  initReveal(document);

  /* ---------------------------------------------------------
     Horizontal carousels (new arrivals, recommendations, etc.)
     --------------------------------------------------------- */
  /* Shared "scroll this horizontally with a mouse" helper — click-and-drag,
     like grabbing a filmstrip. Deliberately does NOT touch the wheel event:
     hijacking vertical wheel scroll into horizontal movement means the
     moment a visitor's cursor happens to be over a product image while
     scrolling down the page, the page stops scrolling entirely — which is
     far worse than the mouse-wheel-can't-scroll-horizontally problem it
     was meant to solve. Drag is the only mouse gesture wired here; normal
     page scrolling over the carousel is left completely alone. */
  function enableMouseScroll(el) {
    if (!el) return;
    var isDown = false, startX = 0, startScroll = 0, moved = false;
    /* a real click's mousedown-to-mouseup movement is almost never exactly
       0px (hand tremor, trackpad noise), so the old 3px threshold was
       misclassifying plain clicks as drags and swallowing the click that
       should have opened the product — 3px was too eager to call it a
       drag. This has to clear a deliberate distance before it counts. */
    var DRAG_THRESHOLD = 8;
    function step() {
      var card = el.querySelector(':scope > *');
      if (!card) return 0;
      var gap = parseFloat(window.getComputedStyle(el).columnGap) || 0;
      return card.getBoundingClientRect().width + gap;
    }
    /* letting go mid-drag used to leave the track sitting wherever the
       cursor happened to be — often half a card cut off on each edge.
       Snap it to the nearest card boundary once the drag ends, the same
       place native scroll-snap would have settled it. */
    function snapToNearest() {
      var s = step();
      if (!s) return;
      var target = Math.round(el.scrollLeft / s) * s;
      target = Math.max(0, Math.min(target, el.scrollWidth - el.clientWidth));
      el.scrollTo({ left: target, behavior: 'smooth' });
    }
    el.classList.add('mouse-scrollable');
    function endDrag() {
      isDown = false;
      el.classList.remove('is-dragging');
      /* restore the track's normal smooth-scroll behavior (used by the
         arrow buttons and the final snap below) now that the drag itself
         is done setting scrollLeft directly every frame */
      el.style.scrollBehavior = '';
      if (moved) snapToNearest();
    }
    el.addEventListener('mousedown', function (e) {
      /* don't start drag-tracking when the press begins on a real control
         inside the card (size pill, add-to-cart button, the image link
         itself, form fields) — let those handle their own click normally
         instead of risking the drag/swallow logic ever intercepting them */
      if (e.target.closest('a, button, input, select, textarea, label')) return;
      isDown = true; moved = false;
      startX = e.pageX; startScroll = el.scrollLeft;
      el.classList.add('is-dragging');
      /* .scroll-smooth (CSS scroll-behavior:smooth) is great for the arrow
         buttons, but it actively fights a drag: each mousemove reassigns
         scrollLeft, and with smooth behavior every one of those restarts
         a ~300ms eased animation toward a moving target instead of
         tracking the cursor, so the track barely moves and then the
         drag-end snap reads a stale position and springs back toward
         where it started. Forcing instant scrolling for the duration of
         the drag (inline style beats the class) makes it track 1:1. */
      el.style.scrollBehavior = 'auto';
    });
    window.addEventListener('mousemove', function (e) {
      if (!isDown) return;
      var dx = e.pageX - startX;
      if (Math.abs(dx) > DRAG_THRESHOLD) moved = true;
      if (moved) el.scrollLeft = startScroll - dx; /* only actually scroll once past the threshold, so a plain click never nudges the track at all */
    });
    window.addEventListener('mouseup', function () {
      if (!isDown) return;
      /* swallow the click that follows a real drag so it doesn't
         accidentally activate a product-card link underneath the cursor */
      if (moved) {
        var swallow = function (e) { e.preventDefault(); e.stopPropagation(); el.removeEventListener('click', swallow, true); };
        el.addEventListener('click', swallow, true);
      }
      endDrag();
    });
    el.addEventListener('mouseleave', function () { if (isDown) endDrag(); });
  }

  /* Same reasoning as initReveal above: callable on a subtree so a
     carousel that arrives after first paint still gets its arrows and
     drag-to-scroll wired up. data-carousel-ready guards against a track
     being initialised twice. */
  function initCarousels(root) {
    qsa('[data-carousel]', root || document).forEach(function (track) {
      if (track.getAttribute('data-carousel-ready')) return;
      track.setAttribute('data-carousel-ready', '1');
      var wrap = track.closest('[data-carousel-wrap]') || document;
      var prev = qs('[data-carousel-prev]', wrap);
      var next = qs('[data-carousel-next]', wrap);
      function step() {
        var card = track.querySelector(':scope > *');
        if (!card) return 300;
        /* the track gap is a theme setting now, so read the real computed
           value instead of assuming the old hard-coded 20px */
        var gap = parseFloat(getComputedStyle(track).columnGap);
        if (isNaN(gap)) gap = 0;
        return card.getBoundingClientRect().width + gap;
      }
      if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: 'smooth' }); });
      if (next) next.addEventListener('click', function () { track.scrollBy({ left: step(), behavior: 'smooth' }); });
      enableMouseScroll(track);
    });
  }
  initCarousels(document);

  /* ---------------------------------------------------------
     Product recommendations — Shopify's own recommendation engine
     (/recommendations/products) rendered through the Section Rendering
     API. The section already ships a server-rendered fallback (shared
     tags → bestsellers → same collection) so the carousel is never empty
     and never blocks first paint; this quietly upgrades it to Shopify's
     picks once they arrive. If the API returns nothing usable the
     fallback is deliberately left alone.
     --------------------------------------------------------- */
  var recWrap = qs('[data-product-recommendations]');
  if (recWrap && recWrap.getAttribute('data-url') && !recWrap.getAttribute('data-loaded')) {
    fetch(recWrap.getAttribute('data-url'))
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (text) {
        if (!text) return;
        var fresh = new DOMParser().parseFromString(text, 'text/html').querySelector('[data-product-recommendations]');
        if (!fresh || !fresh.querySelector('.p-card')) return;
        recWrap.innerHTML = fresh.innerHTML;
        recWrap.setAttribute('data-loaded', '1');
        initReveal(recWrap);
        initCarousels(recWrap);
      })
      .catch(function () {});
  }

  /* ---------------------------------------------------------
     Toast
     --------------------------------------------------------- */
  var toastEl = qs('#toast');
  var toastTimer;
  function toast(msg) {
    if (!toastEl) return;
    qs('#toast-msg', toastEl).textContent = msg;
    toastEl.classList.add('show');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { toastEl.classList.remove('show'); }, 2200);
  }

  /* ---------------------------------------------------------
     Ajax cart — real Shopify Cart API + Section Rendering API
     --------------------------------------------------------- */
  var cartCountEls = function () { return qsa('[data-cart-count]'); };

  function bumpCount() {
    cartCountEls().forEach(function (el) {
      el.classList.add('pop');
      setTimeout(function () { el.classList.remove('pop'); }, 300);
    });
  }

  function refreshCartDrawerMarkup() {
    var url = (ROUTES.cartUrl || '/cart') + '?sections=cart-drawer';
    return fetch(url)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data['cart-drawer'];
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var freshDrawer = doc.getElementById('cart-drawer');
        var liveDrawer = document.getElementById('cart-drawer');
        if (freshDrawer && liveDrawer) {
          var wasOpen = liveDrawer.classList.contains('open');
          liveDrawer.innerHTML = freshDrawer.innerHTML;
          liveDrawer.classList.toggle('open', wasOpen);
        }
      })
      .catch(function () {});
  }

  function refreshCartCount() {
    return fetch((ROUTES.cartUrl || '/cart') + '.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        cartCountEls().forEach(function (el) { el.textContent = cart.item_count; });
        bumpCount();
        return cart;
      })
      .catch(function () {});
  }

  /* keeps the /cart page's own line prices + subtotal live-updating the
     instant quantities change, via the Section Rendering API — no full
     page reload and no manual "update cart" click required */
  function refreshCartPageMarkup() {
    var cartPage = qs('[data-cart-page]');
    if (!cartPage) return Promise.resolve();
    var sectionId = cartPage.getAttribute('data-section-id');
    if (!sectionId) return Promise.resolve();
    var url = (ROUTES.cartUrl || '/cart') + '?sections=' + encodeURIComponent(sectionId);
    return fetch(url)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data[sectionId];
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-cart-page][data-section-id="' + sectionId + '"]');
        var live = document.querySelector('[data-cart-page][data-section-id="' + sectionId + '"]');
        if (fresh && live) live.innerHTML = fresh.innerHTML;
      })
      .catch(function () {});
  }

  /* Cart drawer upsell: recommendations for the most recently added line,
     rendered server-side by sections/cart-upsell.liquid through the
     Recommendations API, minus anything already in the bag. Cached per
     product id, fetched only when the drawer actually opens or its
     contents change, so it never touches page load. */
  var upsellCache = {};
  var upsellRequestId = 0;
  function loadCartUpsell() {
    var cfg = CFG.cartUpsell || {};
    var drawer = document.getElementById('cart-drawer');
    var slot = drawer ? qs('[data-cart-upsell]', drawer) : null;
    if (!cfg.enabled || !slot) return Promise.resolve();
    var anchorId = drawer.getAttribute('data-cart-last-product');
    var inCart = (drawer.getAttribute('data-cart-product-ids') || '').split(',').filter(Boolean);
    if (!anchorId) { slot.innerHTML = ''; return Promise.resolve(); }
    var limit = parseInt(cfg.limit, 10) || 4;
    var paint = function (html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var inner = doc.querySelector('[data-cart-upsell-inner]');
      if (!inner) { slot.innerHTML = ''; return; }
      var items = qsa('[data-upsell-product-id]', inner);
      var kept = 0;
      items.forEach(function (item) {
        if (inCart.indexOf(item.getAttribute('data-upsell-product-id')) > -1 || kept >= limit) { item.parentNode.removeChild(item); return; }
        item.style.setProperty('--i', kept);
        kept++;
      });
      slot.innerHTML = kept ? inner.outerHTML : '';
    };
    if (upsellCache[anchorId]) { paint(upsellCache[anchorId]); return Promise.resolve(); }
    var reqId = ++upsellRequestId;
    var base = ROUTES.productRecommendations || '/recommendations/products';
    var url = base + '?section_id=cart-upsell&product_id=' + encodeURIComponent(anchorId) +
      '&limit=' + Math.min(limit + inCart.length, 10) + '&intent=' + encodeURIComponent(cfg.intent || 'related');
    return fetch(url)
      .then(function (r) { return r.text(); })
      .then(function (html) {
        upsellCache[anchorId] = html;
        if (reqId === upsellRequestId) paint(html);
      })
      .catch(function () {});
  }

  function cartSync(openAfter) {
    return Promise.all([refreshCartCount(), refreshCartDrawerMarkup(), refreshCartPageMarkup()]).then(function () {
      if (openAfter && CFG.cartType !== 'page') openDrawer('cart');
      loadCartUpsell();
    });
  }

  function cartAdd(id, quantity, properties) {
    var body = { id: id, quantity: quantity || 1 };
    if (properties) body.properties = properties;
    return fetch(ROUTES.cartAdd || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (r) { return r.json().then(function (data) { return { ok: r.ok, data: data }; }); })
      .then(function (res) {
        if (!res.ok) { toast(res.data.description || STR.addError || 'Could not add to bag'); return; }
        if (CFG.cartType === 'notification') { toast(STR.addedToBag || 'Added to bag'); return refreshCartCount(); }
        return cartSync(true);
      })
      .catch(function () { toast(STR.genericError || 'Something went wrong'); });
  }

  /* Personalisation / custom text fields on the product page ([data-line-property])
     become line item properties on the cart line. Returns false (and shows
     the field's error) when a required field is empty, an object of
     name -> value otherwise, or undefined when the page has no fields. */
  function collectLineProperties(scope) {
    if (!scope) return undefined;
    var fields = qsa('[data-line-property]', scope);
    if (!fields.length) return undefined;
    var props = {};
    var firstInvalid = null;
    fields.forEach(function (field) {
      var wrap = field.closest('[data-custom-text]');
      var err = wrap ? qs('[data-custom-text-error]', wrap) : null;
      var val = (field.value || '').trim();
      var max = parseInt(field.getAttribute('maxlength'), 10);
      if (max && val.length > max) val = val.slice(0, max);
      if (field.hasAttribute('data-required') && !val) {
        if (err) err.classList.remove('hidden');
        field.setAttribute('aria-invalid', 'true');
        if (!firstInvalid) firstInvalid = field;
        return;
      }
      if (err) err.classList.add('hidden');
      field.removeAttribute('aria-invalid');
      if (val) props[field.getAttribute('data-line-property')] = val;
    });
    if (firstInvalid) { firstInvalid.focus(); return false; }
    return Object.keys(props).length ? props : undefined;
  }
  /* live character counter + clear the error as soon as the shopper types */
  document.addEventListener('input', function (e) {
    var field = e.target.closest('[data-line-property]');
    if (!field) return;
    var wrap = field.closest('[data-custom-text]');
    if (!wrap) return;
    var count = qs('[data-char-count]', wrap);
    if (count) count.textContent = field.value.length;
    var err = qs('[data-custom-text-error]', wrap);
    if (err && field.value.trim()) { err.classList.add('hidden'); field.removeAttribute('aria-invalid'); }
  });

  function buyNow(id, quantity, properties) {
    var buyBody = { id: id, quantity: quantity || 1 };
    if (properties) buyBody.properties = properties;
    return fetch(ROUTES.cartAdd || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(buyBody)
    })
      .then(function (r) { return r.json().then(function (data) { return { ok: r.ok, data: data }; }); })
      .then(function (res) {
        if (!res.ok) { toast(res.data.description || STR.addError || 'Could not add to bag'); return; }
        window.location.href = (ROUTES.cartUrl || '/cart') + '/checkout';
      })
      .catch(function () { toast(STR.genericError || 'Something went wrong'); });
  }

  function cartChange(key, quantity) {
    return fetch(ROUTES.cartChange || '/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function () { return cartSync(false); })
      .catch(function () {});
  }

  document.addEventListener('click', function (e) {
    var addBtn = e.target.closest('[data-add-to-cart]');
    if (addBtn) {
      e.preventDefault();
      if (addBtn.disabled) return;
      var id = addBtn.getAttribute('data-variant-id');
      if (!id) return;
      /* quantity comes from the button's own stepper only (PDP); a card in
         a drawer, menu or grid always adds 1 rather than borrowing the
         product page's stepper value */
      var qtyScope = addBtn.closest('[data-qty-wrap]');
      var qtyWrap = qtyScope ? qtyScope.querySelector('[data-qty-value]') : null;
      var qty = qtyWrap ? parseInt(qtyWrap.getAttribute('data-qty-value'), 10) || 1 : 1;
      var props = collectLineProperties(addBtn.closest('[data-product-section]'));
      if (props === false) return;
      addBtn.disabled = true;
      addBtn.classList.add('is-loading');
      cartAdd(id, qty, props).then(function () {
        addBtn.disabled = false;
        addBtn.classList.remove('is-loading');
      });
      return;
    }
    var incBtn = e.target.closest('[data-cart-qty-inc]');
    if (incBtn) {
      var key1 = incBtn.getAttribute('data-cart-qty-inc');
      var span1 = document.querySelector('[data-cart-qty="' + key1 + '"]');
      var q1 = (span1 ? parseInt(span1.textContent, 10) : 0) + 1;
      if (span1) span1.textContent = q1; /* optimistic — updates instantly, reconciled by section refresh */
      var row1 = incBtn.closest('[data-cart-item]');
      if (row1) row1.classList.add('is-updating');
      cartChange(key1, q1).then(function () { if (row1) row1.classList.remove('is-updating'); });
      return;
    }
    var decBtn = e.target.closest('[data-cart-qty-dec]');
    if (decBtn) {
      var key2 = decBtn.getAttribute('data-cart-qty-dec');
      var span2 = document.querySelector('[data-cart-qty="' + key2 + '"]');
      var q2 = (span2 ? parseInt(span2.textContent, 10) : 1) - 1;
      q2 = Math.max(q2, 0);
      if (span2) span2.textContent = q2;
      var row2 = decBtn.closest('[data-cart-item]');
      if (row2) row2.classList.add('is-updating');
      cartChange(key2, q2).then(function () { if (row2) row2.classList.remove('is-updating'); });
      return;
    }
    var rmBtn = e.target.closest('[data-cart-remove]');
    if (rmBtn) {
      var row3 = rmBtn.closest('[data-cart-item]');
      if (row3) row3.classList.add('is-updating');
      cartChange(rmBtn.getAttribute('data-cart-remove'), 0);
      return;
    }
    var buyBtn = e.target.closest('[data-buy-now]');
    if (buyBtn) {
      var buyId = buyBtn.getAttribute('data-variant-id');
      if (!buyId) return;
      var qtyScope2 = buyBtn.closest('[data-product-section]');
      var qtyWrap2 = qtyScope2 ? qtyScope2.querySelector('[data-qty-value]') : null;
      var qty2 = qtyWrap2 ? parseInt(qtyWrap2.getAttribute('data-qty-value'), 10) || 1 : 1;
      var props2 = collectLineProperties(qtyScope2);
      if (props2 === false) return;
      buyBtn.disabled = true;
      buyBtn.classList.add('is-loading');
      buyNow(buyId, qty2, props2).then(function () {
        buyBtn.disabled = false;
        buyBtn.classList.remove('is-loading');
      });
      return;
    }
    /* product-card quick-add: a size pill was tapped in the hover/overlay
       strip on a card (grid, collection, carousel...). Look up the real
       variant from that card's own product JSON (so it's driven entirely
       by live Shopify data, never hardcoded) and arm the card's own
       add-to-cart button — which then flows through the exact same
       cartAdd()/loading-state code path as everywhere else. */
    var qaPill = e.target.closest('.qa-size-pill');
    if (qaPill) {
      if (qaPill.classList.contains('oos')) return;
      var qaWrap = qaPill.closest('[data-quick-add]');
      if (!qaWrap) return;
      qsa('.qa-size-pill', qaWrap).forEach(function (b) { b.classList.remove('on'); });
      qaPill.classList.add('on');
      /* the product JSON is shared by both the desktop-overlay and
         mobile-below-price copies of the picker on a card (rendered once
         per card, not once per copy), so it lives at the card level, not
         inside this specific [data-quick-add] wrapper */
      var qaCard = qaPill.closest('.p-card');
      var qaJsonEl = qaCard ? qs('[data-qa-product-json]', qaCard) : null;
      var qaBtn = qs('[data-add-to-cart]', qaWrap);
      if (!qaJsonEl || !qaBtn) return;
      var qaProduct;
      try { qaProduct = JSON.parse(qaJsonEl.textContent); } catch (err) { return; }
      var qaValue = qaPill.getAttribute('data-value');
      var qaVariant = qaProduct.variants.find(function (v) { return v.option1 === qaValue; });
      if (!qaVariant) return;
      if (qaVariant.available) {
        qaBtn.disabled = false;
        qaBtn.setAttribute('data-variant-id', qaVariant.id);
        var qaLabelEl = qs('[data-btn-label]', qaBtn);
        if (qaLabelEl) qaLabelEl.textContent = (qaBtn.dataset.labelAdd || STR.cardAdd || 'Add to Bag') + ' — ' + formatMoney(qaVariant.price);
      } else {
        qaBtn.disabled = true;
        qaBtn.setAttribute('data-variant-id', '');
        var qaLabelEl2 = qs('[data-btn-label]', qaBtn);
        if (qaLabelEl2) qaLabelEl2.textContent = qaBtn.dataset.labelSold || STR.cardSold || 'Sold Out';
      }
      return;
    }
    var copyBtn = e.target.closest('[data-copy-link]');
    if (copyBtn) {
      var linkVal = copyBtn.getAttribute('data-copy-link');
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(linkVal).then(function () { toast(STR.linkCopied || 'Link copied'); }).catch(function () {});
      }
      return;
    }
    var scrollBtn = e.target.closest('[data-scroll-to]');
    if (scrollBtn) {
      var target = document.getElementById(scrollBtn.getAttribute('data-scroll-to'));
      if (target) {
        var accBtn = target.previousElementSibling;
        if (accBtn && !target.classList.contains('open')) {
          target.classList.add('open');
          accBtn.classList.add('open');
          accBtn.setAttribute('aria-expanded', 'true');
        }
        setTimeout(function () { target.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 100);
      }
      return;
    }
    /* generic quantity stepper used on PDP (before add-to-cart) */
    var qtyInc = e.target.closest('[data-qty-inc]');
    if (qtyInc) {
      var wrap1 = qtyInc.closest('[data-qty-wrap]');
      var val1 = wrap1.querySelector('[data-qty-value]');
      var next1 = parseInt(val1.getAttribute('data-qty-value'), 10) + 1;
      val1.setAttribute('data-qty-value', next1);
      val1.textContent = next1;
      return;
    }
    var qtyDec = e.target.closest('[data-qty-dec]');
    if (qtyDec) {
      var wrap2 = qtyDec.closest('[data-qty-wrap]');
      var val2 = wrap2.querySelector('[data-qty-value]');
      var next2 = Math.max(1, parseInt(val2.getAttribute('data-qty-value'), 10) - 1);
      val2.setAttribute('data-qty-value', next2);
      val2.textContent = next2;
      return;
    }
  });

  /* ---------------------------------------------------------
     Collection filtering — native Storefront filters, applied
     via the Section Rendering API (no full page reload).
     --------------------------------------------------------- */
  var collectionResults = qs('[data-collection-results]');
  if (collectionResults) {
    var collectionSectionId = collectionResults.getAttribute('data-section-id') || 'main';

    function collectionParams() {
      var params = new URLSearchParams();
      var filterForm = qs('#FilterForm');
      if (filterForm) {
        var fd = new FormData(filterForm);
        var entries = fd.entries ? Array.prototype.slice.call(fd.entries()) : [];
        entries.forEach(function (pair) { params.append(pair[0], pair[1]); });
      }
      var sortSelect = qs('[data-sort-select]');
      if (sortSelect && sortSelect.value) params.set('sort_by', sortSelect.value);
      return params;
    }

    function applyCollectionState(url, pushToHistory) {
      collectionResults.style.opacity = '.4';
      var sep = url.indexOf('?') > -1 ? '&' : '?';
      fetch(url + sep + 'sections=' + collectionSectionId)
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var html = data[collectionSectionId];
          if (!html) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var freshResults = doc.querySelector('[data-collection-results]');
          var freshFilters = doc.querySelector('#filters');
          if (freshResults) {
            collectionResults.innerHTML = freshResults.innerHTML;
            collectionResults.style.opacity = '1';
          }
          var liveFilters = qs('#filters');
          if (freshFilters && liveFilters) {
            var wasOpen = liveFilters.classList.contains('open');
            liveFilters.innerHTML = freshFilters.innerHTML;
            liveFilters.classList.toggle('open', wasOpen);
          }
          if (pushToHistory !== false && window.history && window.history.pushState) {
            window.history.pushState({ collectionState: true }, '', url);
          }
          var top = collectionResults.getBoundingClientRect().top + window.scrollY - 100;
          window.scrollTo({ top: top, behavior: 'smooth' });
        })
        .catch(function () { collectionResults.style.opacity = '1'; window.location.href = url; });
    }

    function applyCollectionFilters(pushToHistory) {
      var params = collectionParams();
      var qsStr = params.toString();
      var url = window.location.pathname + (qsStr ? '?' + qsStr : '');
      applyCollectionState(url, pushToHistory);
    }

    document.addEventListener('change', function (e) {
      if (e.target.closest('#FilterForm') || e.target.matches('[data-sort-select]')) {
        applyCollectionFilters();
      }
    });
    document.addEventListener('submit', function (e) {
      if (e.target.id === 'FilterForm') { e.preventDefault(); applyCollectionFilters(); }
    });
    document.addEventListener('click', function (e) {
      var clearLink = e.target.closest('[data-clear-filters]');
      if (clearLink) { e.preventDefault(); applyCollectionState(clearLink.getAttribute('href'), true); return; }
      var pageLink = e.target.closest('[data-collection-page-link]');
      if (pageLink) { e.preventDefault(); applyCollectionState(pageLink.getAttribute('href'), true); }
    });
    window.addEventListener('popstate', function (e) {
      if (e.state && e.state.collectionState) applyCollectionState(window.location.href, false);
    });
  }

  /* ---------------------------------------------------------
     Predictive search — Storefront JSON endpoint, rendered client-side
     --------------------------------------------------------- */
  var searchInput = qs('#search-input');
  var searchResults = qs('#search-results');
  var searchSuggest = qs('#search-suggest');
  if (searchInput && searchResults && CFG.predictiveSearchEnabled) {
    /* This used to fetch the plain (non-.json) /search/suggest endpoint
       with a section_id param, so Shopify would render our own
       predictive-search.liquid section server-side and we'd just splice
       its markup in. On this store that endpoint reliably comes back
       EMPTY for products the .json endpoint finds correctly with the
       exact same query and options — confirmed by testing both side by
       side (identical params, only the .json vs non-.json path differs).
       Rather than depend on that flaky code path, we fetch the plain JSON
       API — which consistently works — and build the results markup here
       instead of relying on server-side section rendering. */
    /* routes.predictive_search_url comes back as plain /search/suggest on
       this store (no .json extension) — that bare path is the HTML
       section-rendering endpoint, which requires a section_id param and
       422s without one. Forcing .json onto it (regardless of whether
       Shopify happened to include it already) is what actually gets us
       the reliable JSON API on every store, whichever form the route
       object gives us. */
    var predictiveSearchJsonUrl = ROUTES.predictiveSearch || '/search/suggest.json';
    if (!/\.json(\?|$)/.test(predictiveSearchJsonUrl)) predictiveSearchJsonUrl += '.json';
    function renderResults(products, term) {
      if (!products.length) {
        return '<div style="padding-block:2.5rem;text-align:center;">' +
          '<p class="ls-mid uppercase text-clay" style="font-size:12px;">' + escapeHtml((STR.noResults || 'Nothing found for "__TERMS__"').replace('__TERMS__', term)) + '</p>' +
          '<p class="ls-mid uppercase text-clay" style="font-size:10px;opacity:.6;margin-top:.5rem;">' + escapeHtml(STR.noResultsHint || 'Try a product name or colour') + '</p>' +
          '</div>';
      }
      var countLabel = products.length === 1
        ? (STR.resultsCountOne || '1 result')
        : (STR.resultsCountOther || '__COUNT__ results').replace('__COUNT__', products.length);
      var rows = products.map(function (p) {
        var img = p.image ? '<img src="' + escapeHtml(p.image) + (p.image.indexOf('?') > -1 ? '&' : '?') + 'width=200" alt="" width="48" height="64" style="width:3rem;height:4rem;object-fit:cover;object-position:top;flex-shrink:0;" loading="lazy">' : '';
        var vendor = p.vendor ? '<p class="ls-mid uppercase text-clay truncate" style="font-size:10px;margin-top:.125rem;">' + escapeHtml(p.vendor) + '</p>' : '';
        var price = CFG.predictiveSearchShowPrice !== false ? '<span class="font-medium shrink-0" style="font-size:12px;">' + formatMoney(Math.round(parseFloat(p.price) * 100)) + '</span>' : '';
        return '<a href="' + p.url + '" class="sr-item" style="border-bottom:1px solid var(--color-line);">' + img +
          '<div class="flex-1 min-w-0"><p class="ls-mid uppercase font-medium truncate" style="font-size:12px;">' + escapeHtml(p.title) + '</p>' + vendor + '</div>' + price + '</a>';
      }).join('');
      return '<p class="text-clay ls-wide uppercase" style="font-size:10px;padding-block:.75rem;">' + escapeHtml(countLabel) + '</p>' +
        '<div style="border-top:1px solid var(--color-line);">' + rows + '</div>';
    }

    /* Debouncing only limits how OFTEN a request fires — it does nothing
       to guarantee responses come back in the same order the requests
       went out. Anyone typing with a pause longer than the debounce delay
       between letters (very normal for an unfamiliar word) fires more
       than one request, e.g. one for "k" and, moments later, one for
       "kaanan". If the short/simple "k" request happens to resolve AFTER
       the "kaanan" one, it silently overwrites the correct results with
       "nothing found" — which is exactly what looked like "search is
       broken". Tagging each request with an incrementing id and only
       ever rendering the response for the most recently issued one fixes
       this regardless of network timing. */
    var searchRequestId = 0;
    var runSearch = debounce(function (term) {
      term = term.trim();
      var thisRequestId = ++searchRequestId;
      if (!term) { searchResults.innerHTML = ''; if (searchSuggest) searchSuggest.classList.remove('hidden'); return; }
      if (searchSuggest) searchSuggest.classList.add('hidden');
      /* Widening the matched fields beyond the narrow default (title,
         product_type, variants.title) so a product that only matches on
         vendor, tags, or SKU still turns up here the same way it would on
         the full /search results page. */
      var url = predictiveSearchJsonUrl +
        '?q=' + encodeURIComponent(term) +
        '&resources[type]=product&resources[limit]=6' +
        '&resources[options][fields]=title,product_type,variants.title,variants.sku,vendor,tag';
      fetch(url)
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (thisRequestId !== searchRequestId) return; /* a newer search has since started — discard this stale response */
          var products = (data.resources && data.resources.results && data.resources.results.products) || [];
          searchResults.innerHTML = renderResults(products, term);
        })
        .catch(function () {});
    }, 180);
    searchInput.addEventListener('input', function () { runSearch(searchInput.value); });
  }
  qsa('[data-search-chip]').forEach(function (chip) {
    chip.addEventListener('click', function () {
      if (searchInput) { searchInput.value = chip.textContent; searchInput.dispatchEvent(new Event('input')); searchInput.focus(); }
    });
  });

  /* ---------------------------------------------------------
     Cart page: order note (merchant-togglable via
     settings.cart_notes_enable) — persisted to the real Shopify
     cart via /cart/update.js, debounced on input.
     --------------------------------------------------------- */
  var cartNoteEl = qs('[data-cart-note]');
  if (cartNoteEl) {
    var noteStatus = qs('[data-cart-note-status]');
    var saveNote = debounce(function (value) {
      if (noteStatus) noteStatus.textContent = STR.cartNoteSaving || 'Saving…';
      fetch((ROUTES.cartUpdate || '/cart/update.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: value })
      })
        .then(function () { if (noteStatus) noteStatus.textContent = STR.cartNoteSaved || 'Saved'; })
        .catch(function () { if (noteStatus) noteStatus.textContent = ''; });
    }, 500);
    cartNoteEl.addEventListener('input', function () { saveNote(cartNoteEl.value); });
  }

  /* ---------------------------------------------------------
     PDP: pincode / COD delivery checker
     NOTE: stub only — no courier API is integrated yet. This
     just gives instant, plausible feedback client-side. Swap
     the setTimeout block for a real fetch() to a courier
     serviceability endpoint when one is available.
     --------------------------------------------------------- */
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-pincode-form]');
    if (!form) return;
    e.preventDefault();
    var input = form.querySelector('input[name="pincode"]');
    var result = form.parentElement.querySelector('[data-pincode-result]');
    if (!input || !result) return;
    var code = input.value.trim();
    if (!/^[0-9]{6}$/.test(code)) {
      result.textContent = STR.pincodeInvalid || 'Please enter a valid 6-digit pincode';
      return;
    }
    var btn = form.querySelector('button[type="submit"]');
    if (btn) { btn.disabled = true; btn.classList.add('is-loading'); }
    result.textContent = STR.pincodeChecking || 'Checking…';
    setTimeout(function () {
      if (btn) { btn.disabled = false; btn.classList.remove('is-loading'); }
      result.textContent = (STR.pincodeResult || 'Delivers to __CODE__').replace('__CODE__', code) +
        (CFG.codAvailable ? ' · ' + (STR.codAvailable || 'Cash on Delivery available') : '');
    }, 500);
  });

  /* ---------------------------------------------------------
     PDP: gallery, variant picker, sticky ATC
     --------------------------------------------------------- */
  var pdp = qs('[data-product-section]');
  if (pdp) {
    /* desktop thumb gallery */
    var mainImg = qs('[data-gal-main] img', pdp);
    var thumbs = qsa('[data-gal-thumb]', pdp);
    /* Quietly preload every full-size gallery image in the background once
       the page itself has finished loading — not eagerly alongside the
       critical first paint, so it never competes with or slows down the
       initial page load. By the time the shopper actually clicks a thumb,
       arrow, or swatch, the full-res image is already cached and the swap
       is instant instead of waiting on a fresh network fetch. */
    var galWarmed = false;
    function warmGallery() {
      if (galWarmed) return;
      galWarmed = true;
      thumbs.forEach(function (t) {
        var full = t.getAttribute('data-full');
        if (full) { var pre = new Image(); pre.src = full; }
      });
    }
    /* Warm the other gallery images only once the shopper shows intent
       (pointer over the gallery, a touch, or a thumb focus) or, failing
       that, when the browser is idle a few seconds after load. Never as
       part of the initial page load, so audits and real first views only
       pay for the one image on screen. */
    var galRegion = qs('[data-gal-main]', pdp) || pdp;
    ['mouseenter', 'touchstart', 'focusin'].forEach(function (ev) {
      galRegion.addEventListener(ev, warmGallery, { passive: true, once: true });
      var thumbCol = thumbs[0] && thumbs[0].parentElement;
      if (thumbCol) thumbCol.addEventListener(ev, warmGallery, { passive: true, once: true });
    });
    window.addEventListener('load', function () {
      if ('requestIdleCallback' in window) requestIdleCallback(warmGallery, { timeout: 6000 });
      else setTimeout(warmGallery, 4000);
    });
    /* the main image is rendered with a srcset; a plain src swap is ignored
       while srcset is present, so drop it when the shopper changes image */
    function swapMain(src) {
      if (!mainImg) return;
      mainImg.style.opacity = 0;
      setTimeout(function () {
        mainImg.removeAttribute('srcset');
        mainImg.removeAttribute('sizes');
        mainImg.src = src;
        mainImg.onload = function () { mainImg.style.opacity = 1; };
      }, 200);
    }
    var galIdx = 0;
    function setGal(i) {
      if (!thumbs.length) return;
      galIdx = (i + thumbs.length) % thumbs.length;
      var full = thumbs[galIdx].getAttribute('data-full');
      if (mainImg && full) swapMain(full);
      thumbs.forEach(function (t, j) { t.classList.toggle('on', j === galIdx); });
      var mobTrack = qs('[data-gal-track]', pdp);
      if (mobTrack) mobTrack.children[galIdx] && mobTrack.scrollTo({ left: mobTrack.children[galIdx].offsetLeft, behavior: 'smooth' });
    }
    thumbs.forEach(function (t, i) { t.addEventListener('click', function () { setGal(i); }); });
    var galPrev = qs('[data-gal-prev]', pdp), galNext = qs('[data-gal-next]', pdp);
    if (galPrev) galPrev.addEventListener('click', function () { setGal(galIdx - 1); });
    if (galNext) galNext.addEventListener('click', function () { setGal(galIdx + 1); });

    /* mobile swipe dots */
    var galTrack = qs('[data-gal-track]', pdp);
    var galDots = qs('[data-gal-dots]', pdp);
    if (galTrack && galDots) {
      var mCount = galTrack.children.length;
      galDots.innerHTML = Array.from({ length: mCount }).map(function (_, i) {
        return '<span class="gal-dot' + (i === 0 ? ' on' : '') + '"></span>';
      }).join('');
      var mDots = qsa('.gal-dot', galDots);
      galTrack.addEventListener('scroll', debounce(function () {
        var i = Math.round(galTrack.scrollLeft / galTrack.clientWidth);
        mDots.forEach(function (d, j) { d.classList.toggle('on', j === i); });
      }, 80));
    }

    /* lightbox / zoom viewer */
    var lightbox = qs('#lightbox');
    if (lightbox) {
      var lightboxImg = qs('#lightbox-img', lightbox);
      var lightboxSources = thumbs.length
        ? thumbs.map(function (t) { return (t.getAttribute('data-full') || '').replace(/width=\d+/, 'width=1800'); })
        : (mainImg ? [mainImg.src] : []);
      var lightboxIdx = 0;
      function setLightbox(i) {
        if (!lightboxSources.length) return;
        lightboxIdx = (i + lightboxSources.length) % lightboxSources.length;
        lightboxImg.src = lightboxSources[lightboxIdx];
      }
      function openLightbox() {
        setLightbox(galIdx || 0);
        lightbox.classList.add('open');
        document.body.classList.add('locked');
      }
      function closeLightbox() {
        lightbox.classList.remove('open');
        document.body.classList.remove('locked');
      }
      qsa('[data-lightbox-open]', pdp).forEach(function (el) { el.addEventListener('click', openLightbox); });
      var lbClose = qs('[data-lightbox-close]', lightbox);
      if (lbClose) lbClose.addEventListener('click', closeLightbox);
      var lbPrev = qs('[data-lightbox-prev]', lightbox), lbNext = qs('[data-lightbox-next]', lightbox);
      if (lbPrev) lbPrev.addEventListener('click', function () { setLightbox(lightboxIdx - 1); });
      if (lbNext) lbNext.addEventListener('click', function () { setLightbox(lightboxIdx + 1); });
      lightbox.addEventListener('click', function (e) { if (e.target === lightbox) closeLightbox(); });
      document.addEventListener('keydown', function (e) {
        if (!lightbox.classList.contains('open')) return;
        if (e.key === 'Escape') closeLightbox();
        if (e.key === 'ArrowLeft') setLightbox(lightboxIdx - 1);
        if (e.key === 'ArrowRight') setLightbox(lightboxIdx + 1);
      });
    }

    /* variant picker */
    var productJsonEl = qs('[data-product-json]', pdp);
    var product = null;
    try { product = JSON.parse(productJsonEl.textContent); } catch (e) { product = null; }

    var selectedOptions = {};
    qsa('[data-option-name]', pdp).forEach(function (group) {
      var name = group.getAttribute('data-option-name');
      var selected = qs('.on', group);
      if (selected) selectedOptions[name] = selected.getAttribute('data-value');
    });

    function findVariant() {
      if (!product) return null;
      /* a product with only the default "Title / Default Title" variant has
         no real customer-facing options, so no [data-option-name] swatch/
         pill group ever gets rendered for it and selectedOptions.Title is
         never populated by a click — without this early return the
         .every() below would always fail and the sole variant could never
         be found, permanently stuck on the "Select options" state */
      if (product.variants.length === 1) return product.variants[0];
      return product.variants.find(function (v) {
        return product.options.every(function (optName, i) {
          var key = 'option' + (i + 1);
          return v[key] === selectedOptions[optName];
        });
      });
    }

    function updateGalleryForVariant(variant) {
      if (!variant || !variant.featured_image || !variant.featured_image.src) return;
      var targetSrc = variant.featured_image.src.split('?')[0];
      var matchIdx = thumbs.findIndex(function (t) {
        var full = t.getAttribute('data-full');
        return full && full.split('?')[0].indexOf(targetSrc.split('/').pop().split('.')[0]) > -1;
      });
      if (matchIdx > -1) { setGal(matchIdx); return; }
      /* variant has an image not among the gallery thumbs — show it directly */
      if (mainImg) {
        var src = variant.featured_image.src;
        var sep = src.indexOf('?') > -1 ? '&' : '?';
        swapMain(src + sep + 'width=1200');
      }
    }

    /* keep the URL in sync with the selected variant (?variant=12345), the
       same convention Shopify's own themes (Dawn, Impulse) use. This is
       what makes "share this link" / browser back-forward / a page reload
       land back on the exact variant a shopper had open, instead of
       silently resetting to whichever variant happens to be first. Uses
       replaceState (not pushState) so clicking through five sizes doesn't
       fill up the back button with five separate history entries. */
    function updateVariantUrl(variant) {
      if (!variant || !window.history || !window.history.replaceState) return;
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({ variantId: variant.id }, '', url.toString());
    }

    function updateForVariant() {
      var variant = findVariant();
      var atc = qs('[data-add-to-cart]', pdp);
      /* write into the inner [data-btn-label] span, never the button's own
         textContent — the button also holds the .btn-spinner element for
         the loading state, and textContent would silently delete it the
         first time a shopper touched a variant option */
      var atcLabel = atc ? qs('[data-btn-label]', atc) : null;
      var priceEl = qs('[data-price]', pdp);
      var sizeError = qs('[data-size-error]', pdp);
      updateGalleryForVariant(variant);
      if (variant) updateVariantUrl(variant);
      var allChosen = product && (product.variants.length === 1 || product.options.every(function (o) { return selectedOptions[o]; }));
      if (!allChosen) {
        if (atc) {
          atc.disabled = true;
          atc.setAttribute('data-variant-id', '');
          if (atcLabel) atcLabel.textContent = atc.dataset.labelSelect || STR.selectOptions || 'Select options';
        }
        return;
      }
      if (sizeError) sizeError.classList.add('hidden');
      if (variant && variant.available) {
        if (atc) {
          atc.disabled = false;
          atc.setAttribute('data-variant-id', variant.id);
          if (atcLabel) atcLabel.textContent = (atc.dataset.labelAdd || STR.addToCart || 'Add to bag') + ' — ' + formatMoney(variant.price);
        }
        if (priceEl) priceEl.textContent = formatMoney(variant.price);
      } else if (variant) {
        if (atc) {
          atc.disabled = true;
          atc.setAttribute('data-variant-id', variant.id);
          if (atcLabel) atcLabel.textContent = atc.dataset.labelSold || STR.soldOut || 'Sold out';
        }
      }
    }

    /* recently viewed — remember this product for the recently-viewed section */
    if (product && product.handle) {
      try {
        var seen = JSON.parse(localStorage.getItem('strangeshrew:recentlyViewed') || '[]');
        seen = seen.filter(function (h) { return h !== product.handle; });
        seen.unshift(product.handle);
        localStorage.setItem('strangeshrew:recentlyViewed', JSON.stringify(seen.slice(0, 12)));
      } catch (e) {}
    }

    qsa('[data-option-name]', pdp).forEach(function (group) {
      group.addEventListener('click', function (e) {
        var opt = e.target.closest('[data-value]');
        if (!opt || opt.classList.contains('oos')) return;
        var name = group.getAttribute('data-option-name');
        qsa('[data-value]', group).forEach(function (b) { b.classList.remove('on'); });
        opt.classList.add('on');
        selectedOptions[name] = opt.getAttribute('data-value');
        var label = qs('[data-option-label]', group);
        if (label) label.textContent = opt.getAttribute('data-value');
        updateForVariant();
      });
    });
    updateForVariant();

    /* browser back/forward: if the URL's ?variant= changed (e.g. the
       shopper went back after clicking through a couple of sizes), pull
       the option buttons back into sync with it instead of leaving stale
       selections on screen */
    window.addEventListener('popstate', function () {
      if (!product) return;
      var params = new URLSearchParams(window.location.search);
      var vId = params.get('variant');
      if (!vId) return;
      var variant = product.variants.find(function (v) { return String(v.id) === String(vId); });
      if (!variant) return;
      product.options.forEach(function (name, i) {
        selectedOptions[name] = variant['option' + (i + 1)];
      });
      qsa('[data-option-name]', pdp).forEach(function (group) {
        var name = group.getAttribute('data-option-name');
        qsa('[data-value]', group).forEach(function (b) {
          b.classList.toggle('on', b.getAttribute('data-value') === selectedOptions[name]);
        });
        var label = qs('[data-option-label]', group);
        if (label) label.textContent = selectedOptions[name];
      });
      updateForVariant();
    });

    /* sticky mobile add-to-cart bar */
    var stickyBar = qs('#sticky-atc');
    var mainAtc = qs('[data-main-atc-anchor]', pdp);
    if (stickyBar && mainAtc && 'IntersectionObserver' in window) {
      var stickyIo = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { stickyBar.classList.toggle('show', !en.isIntersecting); });
      }, { threshold: 0 });
      stickyIo.observe(mainAtc);
    }
    var stickyBtn = qs('#sticky-atc-btn');
    if (stickyBtn) stickyBtn.addEventListener('click', function () {
      var mainBtn = qs('[data-add-to-cart]', pdp);
      if (mainBtn) mainBtn.click();
    });
  }

  /* ---------------------------------------------------------
     Recently viewed products
     --------------------------------------------------------- */
  var recentlyViewedGrid = qs('[data-recently-viewed-grid]');
  if (recentlyViewedGrid) {
    var currentHandle = recentlyViewedGrid.getAttribute('data-current-handle') || '';
    var rvLimit = parseInt(recentlyViewedGrid.getAttribute('data-limit'), 10) || 8;
    var handles = [];
    try { handles = JSON.parse(localStorage.getItem('strangeshrew:recentlyViewed') || '[]'); } catch (e) { handles = []; }
    handles = handles.filter(function (h) { return h && h !== currentHandle; }).slice(0, rvLimit);

    if (!handles.length) {
      var wrapEl = recentlyViewedGrid.closest('[data-recently-viewed]');
      if (wrapEl) wrapEl.style.display = 'none';
    } else {
      Promise.all(handles.map(function (h) {
        return fetch('/products/' + h + '.js').then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; });
      })).then(function (products) {
        /* Markup here is deliberately identical to snippets/product-card.liquid
           (.p-card > .card-media + .p-card-info) so recently-viewed cards
           pick up exactly the same global spacing, padding and type scale
           as every other product grid on the store — they used to render a
           bare title/price row with no padding in the info area. */
        var html = products.filter(Boolean).map(function (p) {
          var img = p.images && p.images[0] ? p.images[0] : '';
          var sep = img.indexOf('?') > -1 ? '&' : '?';
          var price = ((p.price_varies ? p.price_min : p.price) / 100).toFixed(2);
          var moneyFmt = (CFG.moneyFormat || '₹{{amount}}').replace(/\{\{\s*amount\s*\}\}/, price) + (p.price_varies ? '+' : '');
          var safeTitle = escapeHtml(p.title || '');
          var url = '/products/' + p.handle;
          return '<article class="p-card">' +
            '<div class="card-media aspect-3-4 bg-blush">' +
              '<a href="' + url + '" aria-label="' + safeTitle + '">' +
                (img ? '<img class="img-a" src="' + img + sep + 'width=700" alt="' + safeTitle + '" loading="lazy">' : '') +
              '</a>' +
            '</div>' +
            '<a href="' + url + '" class="p-card-info">' +
              '<div class="min-w-0">' +
                (p.type && CFG.cardShowType !== false ? '<p class="p-card-type truncate ls-wide uppercase text-clay">' + escapeHtml(p.type) + '</p>' : '') +
                '<h3 class="p-card-title truncate ls-mid uppercase font-medium">' + escapeHtml(p.title || '') + '</h3>' +
              '</div>' +
              '<p class="p-card-price font-medium shrink-0">' + moneyFmt + '</p>' +
            '</a>' +
          '</article>';
        }).join('');
        recentlyViewedGrid.innerHTML = html;
      }).catch(function () {});
    }
  }

  /* ---------------------------------------------------------
     Video section: no native controls at all — this custom button is
     the only playback affordance, and clicking the video itself (once
     playing) pauses it again. The button fades out via .is-playing.
     --------------------------------------------------------- */
  /* Autoplaying background video: pause it while it's scrolled off-screen
     and resume when it comes back into view, so it isn't silently
     burning CPU/battery for a video nobody's actually looking at. The
     `autoplay` attribute (set in the section's video_tag) handles the
     initial start once the browser allows it. */
  if ('IntersectionObserver' in window) {
    var videoIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var video = qs('video', entry.target);
        if (!video) return;
        if (entry.isIntersecting) { video.play().catch(function () {}); } else { video.pause(); }
      });
    }, { threshold: 0.25 });
    qsa('[data-video-trigger]').forEach(function (wrap) { videoIO.observe(wrap); });
  }

  /* ---------------------------------------------------------
     Page-load state
     --------------------------------------------------------- */
  requestAnimationFrame(function () { requestAnimationFrame(function () { document.body.classList.add('loaded'); }); });
})();
